/**
 * Moteur de la phase de dessin. Assemble ArtworkRenderer, ZoneCanvas,
 * Compositor et StrokeCapture, et detient tout l'etat haute frequence : trait en
 * cours, pointeur, viewport. Rien de tout cela ne passe par React.
 *
 * React n'est prevenu que sur des evenements rares (fin de trait, undo, ajout
 * d'une couleur a la palette) via les callbacks, pour rafraichir la toolbar.
 */
import {
  BRUSH_SIZES,
  DEFAULT_PALETTE,
  DRAW_ZOOM_RATIO,
  UNDO_STACK_SIZE,
  ZONE_UNITS,
  type DrawTool,
  type DrawZone,
  type Drawing,
  type Stroke,
} from '@retusch/shared';
import { ArtworkRenderer } from './canvas/ArtworkRenderer.js';
import { Compositor } from './canvas/Compositor.js';
import { StrokeCapture } from './canvas/StrokeCapture.js';
import { ZoneCanvas } from './canvas/ZoneCanvas.js';
import {
  type Rect,
  type Size,
  type Vec2,
  distanceToRect,
  fitScale,
  focusViewport,
  isInsideZone,
  normalizedToZone,
  zoneToRect,
} from './canvas/geometry.js';
import {
  MAX_BRUSH_SIZE,
  MIN_BRUSH_SIZE,
  MAX_POINT_DISTANCE,
  POINT_SPACING_BRUSH_RATIO,
  POINT_SPACING_PX,
  OVERVIEW_MS,
  PAN_MARGIN_PX,
  PULSE_PERIOD_MS,
  ZOOM_ANIM_MS,
  ZONE_CANVAS_WIDTH,
} from './canvas/renderConfig.js';
import type { DevOptionsStore } from './devOptions.js';
import { naturalSize } from './artwork.js';

export type ToolId = DrawTool | 'eyedropper' | 'pan';
export type DrawingPhase = 'overview' | 'drawing' | 'frozen';

const GOLD = '#e8b84b';

/**
 * Loupe tactile : cote a l'ecran, grossissement, marge au bord.
 *
 * Le grossissement est volontairement FAIBLE. A 2,4 elle montrait un carre de
 * 48 px autour du doigt : on y voyait le trait de tres pres, mais plus rien de
 * ce qui l'entoure, et c'est pourtant l'entourage qui dit si la retouche se
 * fond. Divise par deux, elle couvre presque 100 px et redonne ce contexte.
 */
const LOUPE_SIZE = 116;
const LOUPE_ZOOM = 1.2;
const LOUPE_MARGIN = 12;

export interface DrawingEngineCallbacks {
  onPaletteChange?(palette: readonly string[]): void;
  onHistoryChange?(state: { strokeCount: number; canUndo: boolean }): void;
  onPhaseChange?(phase: DrawingPhase): void;
  /** Une couleur vient d'etre prelevee : la toolbar doit la selectionner. */
  /**
   * Couleur retenue. `colorIndex` vaut -1 pour un choix libre pas encore
   * inscrit dans la palette ; `color` porte alors la teinte a afficher.
   */
  onColorPicked?(colorIndex: number, color: string): void;
  /**
   * Zoom courant (1 = tableau ajuste a la vue). Emis a cadence bridee : le
   * curseur de zoom doit suivre un pincement, mais React n'a pas a se rendre a
   * chaque image pour ca.
   */
  onZoomChange?(factor: number): void;
  /**
   * Le curseur est-il HORS de la zone a redessiner ?
   *
   * Le moteur est seul a le savoir (il connait le cadrage courant), et
   * l'interface en a besoin pour changer de curseur : hors de la zone, le
   * pinceau et le pot deplacent la vue, la main doit donc s'afficher. Emis
   * uniquement quand la reponse CHANGE, pas a chaque mouvement de souris.
   */
  onOutsideZoneChange?(outside: boolean): void;
}

/** Cadence maximale des notifications de zoom. */
const ZOOM_REPORT_MS = 120;

export interface DrawingEngineOptions {
  readonly canvas: HTMLCanvasElement;
  readonly image: HTMLImageElement;
  readonly zone: DrawZone;
  readonly dev: DevOptionsStore;
  /** Passe l'intro et cadre directement (mode reduced-motion). */
  readonly skipIntro: boolean;
  /**
   * Mode collaboratif : ce que les joueurs precedents ont laisse sur ce
   * tableau, dans l'ordre, chacun avec sa palette.
   *
   * Fond FIGE : on dessine par-dessus, l'undo n'y touche jamais. Defaire le
   * trait d'un autre n'aurait aucun sens, et ce n'est de toute facon pas le
   * mien a defaire.
   */
  readonly base?: ReadonlyArray<{ strokes: readonly Stroke[]; palette: readonly string[] }>;
}

interface LiveStroke {
  readonly tool: DrawTool;
  readonly colorIndex: number;
  readonly size: number;
  readonly points: Vec2[];
}

export class DrawingEngine {
  private readonly options: DrawingEngineOptions;
  private readonly callbacks: DrawingEngineCallbacks;

  private readonly renderer: ArtworkRenderer;
  private readonly compositor: Compositor;
  private readonly zoneCanvas: ZoneCanvas;
  private readonly capture: StrokeCapture;
  private readonly imageSize: Size;
  private readonly zoneRect: Rect;

  private palette: string[] = [...DEFAULT_PALETTE];
  private strokes: Stroke[] = [];
  private live: LiveStroke | null = null;
  private undoBudget = 0;

  private tool: ToolId = 'brush';
  private colorIndex = 0;
  /** Couleur libre en attente d'inscription, quand colorIndex vaut -1. */
  private customColor: string | null = null;
  private brushSizeIndex = 1;
  /** Taille personnalisee (unites zone). null = on suit les 3 presets. */
  private customSize: number | null = null;

  private phase: DrawingPhase = 'overview';
  private cursor: Vec2 | null = null;
  /** Dernier etat annonce a l'interface (cf. onOutsideZoneChange). */
  private outsideZone = false;
  /** Loupe active : vrai sur ecran tactile, ou la main cache ce qu'elle trace. */
  private loupe = false;
  private introTimer = 0;
  private unsubscribeDev: (() => void) | null = null;

  constructor(options: DrawingEngineOptions, callbacks: DrawingEngineCallbacks = {}) {
    this.options = options;
    this.callbacks = callbacks;

    this.imageSize = naturalSize(options.image);
    this.zoneRect = zoneToRect(options.zone, this.imageSize);

    this.renderer = new ArtworkRenderer(options.canvas, this.imageSize);
    this.compositor = new Compositor(
      options.image,
      this.imageSize,
      options.zone,
      ZONE_CANVAS_WIDTH,
    );
    this.zoneCanvas = new ZoneCanvas(this.zoneRect.height / this.zoneRect.width, this.palette);
    if (options.base !== undefined && options.base.length > 0) {
      this.zoneCanvas.setBase(options.base);
    }

    this.capture = new StrokeCapture(
      {
        element: options.canvas,
        mode: 'draw',
        toView: (x, y) => this.renderer.clientToView(x, y),
        toZone: (x, y) => this.toZonePoint(x, y),
        minPointDistance: () => this.pointSpacing(),
        /**
         * L'outil main desarme le dessin : un doigt/curseur deplace la vue.
         *
         * LOIN DE LA ZONE, le pinceau et le pot font pareil. Ils n'y peignent
         * rien (tout est ecrete a la zone) : un appui y armait donc un trait
         * fantome, et le seul moyen de se deplacer etait de changer d'outil
         * puis de revenir. Refuser le dessin ici suffit, le deplacement est le
         * comportement par defaut d'un pointeur qui ne dessine pas.
         *
         * LOIN, et non « dehors » : le basculement se faisait sur le liseré au
         * pixel pres, alors que c'est le long du bord qu'on travaille le plus.
         * PAN_MARGIN_PX laisse deborder avant de rendre la main.
         */
        canDraw: (zonePoint) =>
          this.phase === 'drawing' &&
          this.tool !== 'eyedropper' &&
          this.tool !== 'pan' &&
          this.pxOutsideZoneFromUnit(zonePoint) <= PAN_MARGIN_PX,
      },
      {
        onStrokeStart: (point) => this.beginStroke(point),
        onStrokePoints: (points, tip) => this.extendStroke(points, tip),
        onStrokeEnd: (tip) => this.endStroke(tip),
        onStrokeCancel: () => this.cancelStroke(),
        onGesture: (delta) => this.applyGesture(delta),
        onHover: (point) => this.setCursor(point),
        onTap: (view) => this.handleTap(view),
        onDoubleTap: () => this.toggleZoom(),
      },
    );
  }

  // -------------------------------------------------------------------------
  // Cycle de vie
  // -------------------------------------------------------------------------

  start(): void {
    this.capture.attach();
    this.renderer.fit();
    this.renderer.start(this.render);

    // Une bascule de dev doit repeindre : le feathering est cuit dans le calque
    // de zone, il faut donc aussi l'invalider.
    this.unsubscribeDev = this.options.dev.subscribe(() => {
      this.compositor.invalidateZone();
      this.renderer.markDirty();
    });

    if (this.options.skipIntro) {
      this.focusZone(0);
      return;
    }
    this.introTimer = window.setTimeout(() => this.focusZone(ZOOM_ANIM_MS), OVERVIEW_MS);
  }

  destroy(): void {
    window.clearTimeout(this.introTimer);
    window.clearTimeout(this.zoomTrailing);
    this.unsubscribeDev?.();
    this.unsubscribeDev = null;
    this.capture.detach();
    this.renderer.stop();
  }

  resize(): void {
    this.renderer.resize();
    if (this.phase === 'overview') return;

    // On NE recadre PAS sur la zone. Le canvas change de taille pour des
    // raisons qui n'ont rien a voir avec le joueur : barre d'outils qui
    // grandit, panneau de couleurs qui s'ouvre, tiroir des scores, barre
    // d'URL du telephone qui se retracte. Recadrer a chaque fois lui reprenait
    // le placement qu'il venait de choisir, sans qu'il comprenne pourquoi.
    //
    // Tant qu'il n'a touche a rien, en revanche, le recadrage est un service :
    // c'est le cadrage automatique du debut de manche qui suit la nouvelle
    // boite. Meme regle que RevealEngine.
    if (!this.viewTouched) {
      this.focusZone(0);
      return;
    }
    // Sa vue est conservee, simplement ramenee dans les bornes de la nouvelle
    // boite : sans ce recalage, retrecir le canvas laisserait le tableau a
    // moitie dehors.
    this.renderer.setViewport(this.renderer.getViewport());
  }

  /**
   * Loupe : montre ce que le doigt recouvre.
   *
   * Reservee aux ecrans tactiles. A la souris elle n'a aucun objet : rien ne
   * masque le trait, et un carre flottant volerait de la place au tableau.
   */
  setLoupe(enabled: boolean): void {
    this.loupe = enabled;
  }

  /** Fin du timer ou soumission : plus aucun trait possible. */
  freeze(): void {
    // On CLOT le trait en cours, on ne le jette pas : la manche se termine
    // peut-etre au milieu d'un geste, et ce geste fait partie du dessin. Il
    // etait perdu, alors que son auteur le voyait a l'ecran.
    this.capture.finishStroke();
    this.setPhase('frozen');
  }

  private focusZone(durationMs: number): void {
    // Aucun decalage vertical : la toolbar est un frere du canvas dans le flux,
    // elle ne le recouvre pas. Compenser sa hauteur poussait la zone hors du
    // cadre par le haut.
    const target = focusViewport(this.zoneRect, this.renderer.view, DRAW_ZOOM_RATIO);
    void this.renderer.animateTo(target, durationMs).then(() => {
      if (this.phase === 'overview') this.setPhase('drawing');
    });
  }

  private setPhase(phase: DrawingPhase): void {
    if (this.phase === phase) return;
    this.phase = phase;
    this.renderer.markDirty();
    this.callbacks.onPhaseChange?.(phase);
  }

  // -------------------------------------------------------------------------
  // Outils
  // -------------------------------------------------------------------------

  setTool(tool: ToolId): void {
    this.tool = tool;
    this.renderer.markDirty();
  }

  setColorIndex(index: number): void {
    this.colorIndex = index;
    // Revenir a une pastille annule le choix libre en attente.
    this.customColor = null;
    this.renderer.markDirty();
  }

  setBrushSizeIndex(index: number): void {
    this.brushSizeIndex = index;
    this.customSize = null;
    this.renderer.markDirty();
  }

  /** Taille exacte, en unites zone (cf. ZONE_UNITS). Prime sur les presets. */
  setBrushSize(size: number): void {
    this.customSize = Math.min(Math.max(Math.round(size), MIN_BRUSH_SIZE), MAX_BRUSH_SIZE);
    this.renderer.markDirty();
  }

  getBrushSize(): number {
    return this.currentSize();
  }

  getPalette(): readonly string[] {
    return this.palette;
  }

  /**
   * Fond de la zone. Repeindre avec lui EST l'effacement : c'est ce que la
   * pastille de fin de palette propose, a la place de l'ancien blanc.
   */
  getBaseColor(): string {
    return this.compositor.baseColor;
  }

  getDrawing(): Drawing {
    return this.strokes;
  }

  undo(): void {
    if (this.undoBudget <= 0 || this.strokes.length === 0) return;
    this.strokes = this.strokes.slice(0, -1);
    this.undoBudget -= 1;
    this.zoneCanvas.replay(this.strokes);
    this.compositor.invalidateZone();
    this.renderer.markDirty();
    this.emitHistory();
  }

  private emitHistory(): void {
    this.callbacks.onHistoryChange?.({
      strokeCount: this.strokes.length,
      canUndo: this.undoBudget > 0 && this.strokes.length > 0,
    });
  }

  private currentSize(): number {
    if (this.tool === 'fill') return 0;
    return this.customSize ?? BRUSH_SIZES[this.brushSizeIndex] ?? BRUSH_SIZES[1];
  }

  // -------------------------------------------------------------------------
  // Traits
  // -------------------------------------------------------------------------

  /**
   * A quelle distance de la zone, en pixels D'ECRAN, tombe ce point de l'image ?
   *
   * Zero quand il est dedans. UNE SEULE fonction pour les deux appelants, l'appui
   * et le survol : ils travaillaient dans deux systemes de coordonnees
   * differents, avec chacun son predicat, et deux predicats pour une meme regle
   * finissent toujours par se contredire sur le bord, la ou l'on joue.
   */
  private pxOutsideZone(imagePoint: Vec2): number {
    return distanceToRect(imagePoint, this.zoneRect) * this.renderer.getViewport().scale;
  }

  /** Le meme, depuis un point en unites zone (0-1), ce que donne l'appui. */
  private pxOutsideZoneFromUnit(point: Vec2): number {
    return this.pxOutsideZone({
      x: this.zoneRect.x + point.x * this.zoneRect.width,
      y: this.zoneRect.y + point.y * this.zoneRect.height,
    });
  }

  private toZonePoint(clientX: number, clientY: number): Vec2 {
    const normalized = this.renderer.clientToNormalized(clientX, clientY);
    return {
      x: (normalized.x - this.options.zone.x) / this.options.zone.width,
      y: (normalized.y - this.options.zone.y) / this.options.zone.height,
    };
  }

  private beginStroke(point: Vec2): void {
    // Ni la pipette ni la main ne produisent de trait : canDraw() les exclut
    // deja, ce garde protege les appels directs.
    if (this.tool === 'eyedropper' || this.tool === 'pan') return;

    // Le pot de peinture est un evenement ponctuel : un point, applique tout de
    // suite, pas de trait en cours.
    if (this.tool === 'fill') {
      if (point.x < 0 || point.x > 1 || point.y < 0 || point.y > 1) return;
      this.commitStroke({ tool: 'fill', colorIndex: this.indexFor(), size: 0, points: [point] });
      return;
    }

    this.live = {
      tool: this.tool,
      colorIndex: this.indexFor(),
      size: this.currentSize(),
      points: [point],
    };
    // La pointe part du point d'appui : sans elle, le premier segment du trait
    // s'afficherait avec une frame de retard.
    this.zoneCanvas.setLive(this.live, point);
    this.compositor.invalidateZone();
    this.renderer.markDirty();
  }

  /**
   * Etend le trait en cours.
   *
   * `points` entre dans le dessin ENCODE, `tip` seulement a l'ecran. La
   * distinction est tout le sujet : l'echantillonnage borne le poids du dessin
   * sur le fil, il n'a aucune raison de faire trainer l'encre derriere le
   * curseur. Le trait affiche va donc jusqu'a la position reelle du pointeur,
   * et le dernier segment est remplace par le suivant a la frame d'apres.
   */
  private extendStroke(points: readonly Vec2[], tip: Vec2): void {
    if (this.live === null) return;
    this.live.points.push(...points);
    this.zoneCanvas.setLive(this.live, tip);
    this.compositor.invalidateZone();
    this.renderer.markDirty();
  }

  /**
   * Cloture le trait sur la DERNIERE position du pointeur.
   *
   * Sans elle, le trait fige s'arretait au dernier point retenu : tout ce qui
   * avait ete trace depuis, jusqu'a un espacement complet, disparaissait au
   * relachement. Sur un petit geste, c'etait le geste entier qui se volatilisait
   * alors qu'on l'avait vu a l'ecran.
   */
  private endStroke(tip: Vec2 | null): void {
    if (this.live === null) return;
    const stroke = this.live;
    const last = stroke.points[stroke.points.length - 1];
    if (tip !== null && (last === undefined || last.x !== tip.x || last.y !== tip.y)) {
      stroke.points.push(tip);
    }
    this.live = null;
    this.commitStroke(stroke);
  }

  private cancelStroke(): void {
    if (this.live === null) return;
    this.live = null;
    this.zoneCanvas.setLive(null);
    this.compositor.invalidateZone();
    this.renderer.markDirty();
  }

  private commitStroke(stroke: Stroke): void {
    this.strokes = [...this.strokes, stroke];
    this.undoBudget = Math.min(this.undoBudget + 1, UNDO_STACK_SIZE);
    this.zoneCanvas.commit(stroke);
    this.compositor.invalidateZone();
    this.renderer.markDirty();
    this.emitHistory();
  }

  // -------------------------------------------------------------------------
  // Pipette, gestes, curseur
  // -------------------------------------------------------------------------

  private handleTap(view: Vec2): void {
    if (this.tool !== 'eyedropper') return;

    const rect = this.options.canvas.getBoundingClientRect();
    const normalized = this.renderer.clientToNormalized(view.x + rect.left, view.y + rect.top);

    // DANS la zone, la pipette lit la retouche en cours, pas l'œuvre masquee :
    // celle-ci est precisement ce que le joueur ignore, la prelever serait un
    // triche-couleur. Rien de pose encore = le FOND de la zone, exactement ce
    // que l'on voit a cet endroit.
    const color = isInsideZone(normalized, this.options.zone)
      ? (this.zoneCanvas.sampleAt(normalizedToZone(normalized, this.options.zone)) ??
        this.compositor.baseColor)
      : this.compositor.sampleColor(normalized);

    this.useColor(color);
  }

  /**
   * Selectionne une couleur arbitraire, SANS toucher a la palette.
   *
   * L'inscription dans la palette est differee au premier trait qui l'emploie
   * (cf. `indexFor`). Un selecteur de couleur emet un evenement a chaque
   * mouvement de souris : y inscrire la couleur aussitot creait des dizaines
   * d'entrees pour un seul choix.
   */
  useColor(color: string): void {
    this.customColor = color;
    this.colorIndex = -1;
    this.tool = 'brush';
    this.callbacks.onColorPicked?.(-1, color);
    this.renderer.markDirty();
  }

  /**
   * Index de la couleur courante dans la palette, en l'y ajoutant au besoin.
   *
   * Appele au moment de TRACER, jamais avant : seules les couleurs reellement
   * employees entrent dans la palette declaree avec le dessin, qui est ce qui
   * permet aux autres clients de le rendre avec les bonnes teintes.
   */
  private indexFor(): number {
    if (this.colorIndex >= 0) return this.colorIndex;
    const color = this.customColor ?? this.palette[0] ?? '#000000';

    const existing = this.palette.indexOf(color);
    if (existing >= 0) {
      this.colorIndex = existing;
      return existing;
    }

    // colorIndex est un uint8 : au-dela de 256 entrees la palette ne tient
    // plus dans le format binaire, on recycle donc le dernier emplacement.
    const index = Math.min(this.palette.length, 255);
    this.palette = [...this.palette.slice(0, index), color];
    this.zoneCanvas.setPalette(this.palette);
    this.callbacks.onPaletteChange?.(this.palette);
    this.colorIndex = index;
    return index;
  }

  /** Couleur avec laquelle on peint, palette ou choix libre. */
  currentColor(): string {
    return (this.colorIndex >= 0 ? this.palette[this.colorIndex] : this.customColor) ?? '#000000';
  }

  private applyGesture(delta: { dx: number; dy: number; scale: number; center: Vec2 }): void {
    this.renderer.cancelAnimation();
    if (delta.dx !== 0 || delta.dy !== 0) this.viewTouched = true;
    if (delta.scale !== 1) this.viewTouched = true;
    if (delta.dx !== 0 || delta.dy !== 0) this.renderer.panBy(delta.dx, delta.dy);
    if (delta.scale !== 1) {
      this.renderer.zoomAt(delta.scale, delta.center);
      this.emitZoom();
    }
  }

  // -------------------------------------------------------------------------
  // Zoom pilote (curseur + boutons)
  // -------------------------------------------------------------------------

  /**
   * Espacement minimal entre deux points retenus, en coordonnees zone.
   *
   * Deux termes, et le plus GRAND l'emporte :
   *  - la reactivite : une distance fixe a l'ecran, convertie au zoom courant,
   *    pour que la main declenche le trait au meme geste a toutes les echelles ;
   *  - la fidelite : un quart de la largeur du pinceau, en dessous de quoi les
   *    points supplementaires ne se voient pas mais se paient sur le fil.
   *
   * C'est le pinceau qui borne le cout, et non une constante : un trait fin
   * merite beaucoup de points, un gros trait n'en a aucun usage.
   */
  private pointSpacing(): number {
    const zoneScreenWidth = this.zoneRect.width * this.renderer.getViewport().scale;
    const reactivity = zoneScreenWidth > 0 ? POINT_SPACING_PX / zoneScreenWidth : MAX_POINT_DISTANCE;
    const fidelity = (this.currentSize() / ZONE_UNITS) * POINT_SPACING_BRUSH_RATIO;
    return Math.min(Math.max(reactivity, fidelity), MAX_POINT_DISTANCE);
  }

  /**
   * Double appui : alterne entre le cadrage sur la zone et un pas de recul.
   *
   * Au doigt, c'est le seul geste de zoom qui se fasse d'une main. Le
   * pincement reste disponible pour le reglage fin.
   */
  /** Le joueur a place sa vue lui-meme : on ne la lui reprend plus. */
  private viewTouched = false;

  private toggleZoom(): void {
    const zoomed = this.zoomFactor() > this.fitZoomFactor() * 1.35;
    if (zoomed) {
      void this.renderer.animateTo(
        focusViewport(this.zoneRect, this.renderer.view, DRAW_ZOOM_RATIO),
        ZOOM_ANIM_MS,
      );
    } else {
      this.setZoomFactor(this.zoomFactor() * 2.2);
    }
    this.emitZoom(true);
  }

  /** Zoom du cadrage d'ouverture, reference du double appui. */
  private fitZoomFactor(): number {
    const target = focusViewport(this.zoneRect, this.renderer.view, DRAW_ZOOM_RATIO);
    const base = fitScale(this.renderer.image, this.renderer.view);
    return base > 0 ? target.scale / base : 1;
  }

  /** Zoom courant : 1 = le tableau entier tient dans la vue. */
  zoomFactor(): number {
    const base = fitScale(this.renderer.image, this.renderer.view);
    return base > 0 ? this.renderer.getViewport().scale / base : 1;
  }

  /**
   * Regle le zoom AUTOUR DU CENTRE de la vue. Un pincement zoome sur ce qu'on
   * tient entre les doigts ; un curseur n'a pas de point de visee, et partir du
   * centre est la seule reponse qui ne surprenne personne.
   */
  setZoomFactor(factor: number): void {
    this.viewTouched = true;
    const base = fitScale(this.renderer.image, this.renderer.view);
    const current = this.renderer.getViewport().scale;
    if (base <= 0 || current <= 0) return;

    this.renderer.cancelAnimation();
    const view = this.renderer.view;
    this.renderer.zoomAt((base * factor) / current, { x: view.width / 2, y: view.height / 2 });
    // Force : le viewport est borne, la valeur retenue n'est pas forcement
    // celle demandee, et le curseur doit se caler sur la vraie.
    this.emitZoom(true);
  }

  private zoomEmittedAt = 0;
  private zoomTrailing = 0;

  private emitZoom(force = false): void {
    const listener = this.callbacks.onZoomChange;
    if (listener === undefined) return;

    const now = performance.now();
    if (force || now - this.zoomEmittedAt >= ZOOM_REPORT_MS) {
      this.zoomEmittedAt = now;
      listener(this.zoomFactor());
      return;
    }
    // Fin de geste : sans ce rattrapage, la derniere valeur d'un pincement
    // tomberait dans la fenetre de bridage et le curseur resterait decale.
    if (this.zoomTrailing !== 0) return;
    this.zoomTrailing = window.setTimeout(() => {
      this.zoomTrailing = 0;
      this.zoomEmittedAt = performance.now();
      listener(this.zoomFactor());
    }, ZOOM_REPORT_MS);
  }

  private setCursor(point: Vec2 | null): void {
    this.cursor = point;
    this.renderer.markDirty();
    this.reportOutsideZone(point);
  }

  /**
   * Previent l'interface quand le curseur passe d'un cote a l'autre du liseré.
   *
   * Le curseur sorti de la fenetre (`null`) ne compte pas comme « dehors » :
   * il n'y a plus de curseur a habiller, et le dire ferait clignoter l'icone au
   * moindre passage sur la barre d'outils.
   */
  private reportOutsideZone(point: Vec2 | null): void {
    if (point === null) return;
    // `viewToNormalized` et non `toZonePoint` : le point est deja en
    // coordonnees de vue, et repasser par des coordonnees client relirait la
    // boite du canvas a chaque mouvement de souris.
    const normalized = this.renderer.viewToNormalized(point);
    const outside =
      this.pxOutsideZone({
        x: normalized.x * this.imageSize.width,
        y: normalized.y * this.imageSize.height,
      }) > PAN_MARGIN_PX;
    if (outside === this.outsideZone) return;
    this.outsideZone = outside;
    this.callbacks.onOutsideZoneChange?.(outside);
  }

  // -------------------------------------------------------------------------
  // Rendu
  // -------------------------------------------------------------------------

  private readonly render = (
    ctx: CanvasRenderingContext2D,
    _viewport: unknown,
    now: number,
  ): void => {
    const dev = this.options.dev.value;
    this.compositor.draw(ctx, this.renderer.getViewport(), this.zoneCanvas.canvas, {
      grain: dev.grain,
      feather: dev.feather,
    });

    const rect = this.viewZoneRect();

    if (this.phase === 'overview') {
      this.drawPulsingOutline(ctx, rect, now);
      // La pulsation doit continuer : on redemande une frame.
      this.renderer.markDirty();
    } else {
      // Pas d'assombrissement pendant le dessin. Le jeu consiste a assortir une
      // couleur au tableau : voir l'œuvre 40 % plus sombre que la zone qu'on
      // peint, c'est peindre a l'aveugle et croire qu'on s'est trompe de teinte.
      // Le lisere suffit a situer la zone.
      this.drawZoneOutline(ctx, rect);
    }

    if (dev.showZoneRect) this.drawDevZoneRect(ctx, rect);
    if (dev.touchProbe) this.drawProbe(ctx);
    if (this.phase === 'drawing') this.drawCursor(ctx, rect);
    // EN DERNIER : la loupe recopie ce qui vient d'etre peint. Elle se lit donc
    // sur l'image finale, grain et retouche compris.
    if (this.loupe && this.live !== null) this.drawLoupe(ctx);
  };

  private viewZoneRect(): Rect {
    const topLeft = this.renderer.normalizedToView({
      x: this.options.zone.x,
      y: this.options.zone.y,
    });
    const bottomRight = this.renderer.normalizedToView({
      x: this.options.zone.x + this.options.zone.width,
      y: this.options.zone.y + this.options.zone.height,
    });
    return {
      x: topLeft.x,
      y: topLeft.y,
      width: bottomRight.x - topLeft.x,
      height: bottomRight.y - topLeft.y,
    };
  }

  private drawPulsingOutline(ctx: CanvasRenderingContext2D, rect: Rect, now: number): void {
    const pulse = (Math.sin((now / PULSE_PERIOD_MS) * Math.PI * 2) + 1) / 2;
    ctx.save();
    ctx.strokeStyle = GOLD;
    ctx.globalAlpha = 0.45 + pulse * 0.55;
    ctx.lineWidth = 2 + pulse * 1.5;
    ctx.shadowColor = GOLD;
    ctx.shadowBlur = 8 + pulse * 16;
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    ctx.restore();
  }

  /** Lisere discret : situe la zone sans toucher aux couleurs du tableau. */
  private drawZoneOutline(ctx: CanvasRenderingContext2D, rect: Rect): void {
    ctx.save();
    ctx.strokeStyle = GOLD;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    ctx.restore();
  }

  /**
   * Loupe : un carre agrandi de ce que le doigt recouvre.
   *
   * Recopie la zone du canvas DEJA peinte cette frame, ce qui coute un seul
   * drawImage et garantit qu'elle montre exactement le rendu final.
   *
   * Place FIXE, juste au-dessus du carre a redessiner. Elle sautait d'un coin a
   * l'autre selon le cote ou se trouvait le doigt : on la cherchait des yeux a
   * chaque trait, ce qui coute plus d'attention que le doigt n'en cache. Au
   * meme endroit, on apprend a la regarder sans y penser. Bornee au canvas :
   * quand la zone touche le haut, la loupe s'y appuie plutot que de sortir de
   * l'ecran.
   */
  private drawLoupe(ctx: CanvasRenderingContext2D): void {
    const cursor = this.cursor;
    if (cursor === null) return;

    const view = this.renderer.view;
    const canvas = this.options.canvas;
    const dpr = canvas.width / Math.max(view.width, 1);
    const source = LOUPE_SIZE / LOUPE_ZOOM;

    const zone = this.viewZoneRect();
    const left = Math.min(
      Math.max(zone.x + zone.width / 2 - LOUPE_SIZE / 2, LOUPE_MARGIN),
      Math.max(view.width - LOUPE_SIZE - LOUPE_MARGIN, LOUPE_MARGIN),
    );
    const top = Math.min(
      Math.max(zone.y - LOUPE_MARGIN - LOUPE_SIZE, LOUPE_MARGIN),
      Math.max(view.height - LOUPE_SIZE - LOUPE_MARGIN, LOUPE_MARGIN),
    );

    const sx = Math.min(Math.max(cursor.x - source / 2, 0), Math.max(view.width - source, 0));
    const sy = Math.min(Math.max(cursor.y - source / 2, 0), Math.max(view.height - source, 0));

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(left, top, LOUPE_SIZE, LOUPE_SIZE, 12);
    ctx.fillStyle = '#101014';
    ctx.fill();
    ctx.save();
    ctx.clip();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      canvas,
      sx * dpr,
      sy * dpr,
      source * dpr,
      source * dpr,
      left,
      top,
      LOUPE_SIZE,
      LOUPE_SIZE,
    );
    ctx.restore();

    // Croix centrale : sans elle on ne sait pas quel point de la loupe
    // correspond au doigt.
    const centerX = left + LOUPE_SIZE / 2;
    const centerY = top + LOUPE_SIZE / 2;
    ctx.strokeStyle = '#00000066';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(centerX - 7, centerY);
    ctx.lineTo(centerX + 7, centerY);
    ctx.moveTo(centerX, centerY - 7);
    ctx.lineTo(centerX, centerY + 7);
    ctx.stroke();

    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(left, top, LOUPE_SIZE, LOUPE_SIZE, 12);
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Sonde de dev : cercle magenta au point BRUT du pointeur, croix verte au
   * dernier point RETENU. Si les deux se separent d'autre chose que
   * l'espacement d'echantillonnage, la conversion de coordonnees est en cause.
   */
  private drawProbe(ctx: CanvasRenderingContext2D): void {
    const cursor = this.cursor;
    if (cursor !== null) {
      ctx.save();
      ctx.strokeStyle = '#ff00ff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cursor.x, cursor.y, 10, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    const last = this.live?.points[this.live.points.length - 1];
    if (last === undefined) return;
    const view = this.renderer.normalizedToView({
      x: this.options.zone.x + last.x * this.options.zone.width,
      y: this.options.zone.y + last.y * this.options.zone.height,
    });
    ctx.save();
    ctx.strokeStyle = '#4ade80';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(view.x - 8, view.y);
    ctx.lineTo(view.x + 8, view.y);
    ctx.moveTo(view.x, view.y - 8);
    ctx.lineTo(view.x, view.y + 8);
    ctx.stroke();
    ctx.restore();
  }

  private drawDevZoneRect(ctx: CanvasRenderingContext2D, rect: Rect): void {
    ctx.save();
    ctx.strokeStyle = '#ff00ff';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    ctx.restore();
  }

  /** Curseur desktop : cercle a la taille reelle du pinceau, dans sa couleur. */
  private drawCursor(ctx: CanvasRenderingContext2D, zoneRect: Rect): void {
    const cursor = this.cursor;
    // Hors de la zone, le pinceau deplace la vue : son anneau promettrait un
    // trait qui ne viendra pas.
    if (
      cursor === null ||
      this.outsideZone ||
      this.tool === 'fill' ||
      this.tool === 'eyedropper' ||
      this.tool === 'pan'
    ) {
      return;
    }

    const radius = ((this.currentSize() / ZONE_UNITS) * zoneRect.width) / 2;
    if (radius <= 0) return;

    ctx.save();
    ctx.beginPath();
    ctx.arc(cursor.x, cursor.y, Math.max(radius, 2), 0, Math.PI * 2);
    if (this.tool === 'eraser') {
      ctx.strokeStyle = '#ffffff';
      ctx.setLineDash([4, 3]);
    } else {
      ctx.fillStyle = this.currentColor();
      ctx.globalAlpha = 0.65;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = '#00000066';
    }
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
  }
}
